import { resolveCatalogProductPurchasability } from '../../purchasability'
import { buildProductPathFromIdentity } from '../../routing'
import type { CatalogProduct } from '../../types'
import { getProductImageSources } from '../categoryPageHelpers'
import { computeProductPrice } from './productFilters'

type Props = {
  language: 'it' | 'en'
  loading: boolean
  error: string
  searchTerm: string
  products: CatalogProduct[]
  cartBusy: boolean
  formatCurrency: (value: number) => string
  toLocalizedPath: (path: string) => string
  onAddToCart: (product: CatalogProduct) => void
}

const SearchResultsGrid = ({
  language,
  loading,
  error,
  searchTerm,
  products,
  cartBusy,
  formatCurrency,
  toLocalizedPath,
  onAddToCart,
}: Props) => (
  <section className="space-y-4">
    {loading && products.length === 0 && (
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
        {language === 'en' ? 'Loading search results...' : 'Caricamento risultati...'}
      </div>
    )}

    {loading && products.length > 0 && (
      <p className="text-xs font-medium text-slate-500" role="status">
        {language === 'en' ? 'Updating results...' : 'Aggiornamento risultati...'}
      </p>
    )}

    {!loading && error && (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 shadow-sm">
        {error}
      </div>
    )}

    {!loading && !error && !searchTerm && (
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
        {language === 'en'
          ? 'Type a term in the search bar to start.'
          : 'Scrivi un termine nella barra di ricerca per iniziare.'}
      </div>
    )}

    {!loading && !error && searchTerm && products.length === 0 && (
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
        {language === 'en'
          ? 'No products found with the current filters.'
          : 'Nessun prodotto trovato con i filtri attuali.'}
      </div>
    )}

    {!error && products.length > 0 && (
      <div className={`grid gap-4 transition-opacity md:grid-cols-2 xl:grid-cols-3 ${loading ? 'opacity-60' : 'opacity-100'}`}>
        {products.map((product, index) => {
          const productImage = getProductImageSources(
            product,
            '(min-width: 1280px) 30vw, (min-width: 768px) 46vw, 92vw',
          )
          const eagerImage = index === 0
          const price = computeProductPrice(product)
          const purchasability = resolveCatalogProductPurchasability(product)
          const productPath = buildProductPathFromIdentity({
            publicCode: product.publicCode,
            slug: product.slug,
          })
          return (
            <article
              key={product._id}
              className="group rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <a href={toLocalizedPath(productPath)} className="block">
                <div className="overflow-hidden rounded-xl bg-slate-100">
                  {productImage.src ? (
                    <img
                      src={productImage.src}
                      srcSet={productImage.srcSet}
                      sizes={productImage.sizes}
                      alt={product.name}
                      loading={eagerImage ? 'eager' : 'lazy'}
                      decoding="async"
                      fetchPriority={eagerImage ? 'high' : 'low'}
                      className="h-44 w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                    />
                  ) : (
                    <div className="grid h-44 place-items-center bg-slate-200 text-sm text-slate-500">
                      {language === 'en' ? 'No image' : 'Nessuna immagine'}
                    </div>
                  )}
                </div>
                <div className="space-y-1.5 px-1 pb-2 pt-3">
                  <p className="line-clamp-2 text-base font-bold text-slate-900">{product.name}</p>
                  <p className="line-clamp-2 text-xs text-slate-600">
                    {product.description?.trim() ||
                      (language === 'en'
                        ? 'Open product sheet for full technical details.'
                        : 'Apri la scheda prodotto per i dettagli completi.')}
                  </p>
                </div>
              </a>
              <div className="flex items-center justify-between gap-2 px-1">
                {price !== null ? (
                  <p className="text-sm font-semibold text-emerald-700">{formatCurrency(price)}</p>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  onClick={() => onAddToCart(product)}
                  disabled={cartBusy || !purchasability.isPurchasable}
                  className="rounded-full border border-emerald-300 bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {purchasability.reason === 'out_of_stock'
                    ? language === 'en'
                      ? 'Sold out'
                      : 'Esaurito'
                    : purchasability.isPurchasable
                    ? language === 'en'
                      ? 'Add'
                      : 'Aggiungi'
                    : language === 'en'
                    ? 'Unavailable'
                    : 'Non disponibile'}
                </button>
              </div>
            </article>
          )
        })}
      </div>
    )}
  </section>
)

export default SearchResultsGrid
